// Creative Library API Client - COMPLETE
import { apiClient } from './apiClient';
import type {
  Creative,
  CreativeLibraryResponse,
  CreativeFilters,
  CreativeUsageHistory,
  Tag
} from '../types/creative-library';

export const creativeLibraryApi = {
  // List creatives with filters and pagination
  async list(
    filters: CreativeFilters = {},
    pagination: { page?: number; page_size?: number } = {}
  ): Promise<{ creatives: Creative[]; total: number }> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.platforms?.length) params.append('platforms', filters.platforms.join(','));
    if (filters.creative_types?.length) params.append('creative_types', filters.creative_types.join(','));
    if (filters.performance_tiers?.length) params.append('performance_tiers', filters.performance_tiers.join(','));
    if (filters.statuses?.length) params.append('statuses', filters.statuses.join(','));
    if (filters.tags?.length) params.append('tags', filters.tags.join(','));
    if (filters.date_range?.start) params.append('date_from', filters.date_range.start);
    if (filters.date_range?.end) params.append('date_to', filters.date_range.end);
    if (filters.min_roas !== undefined) params.append('min_roas', filters.min_roas.toString());
    if (filters.max_roas !== undefined) params.append('max_roas', filters.max_roas.toString());
    if (filters.min_ctr !== undefined) params.append('min_ctr', filters.min_ctr.toString());
    if (filters.max_ctr !== undefined) params.append('max_ctr', filters.max_ctr.toString());
    if (filters.min_cpa !== undefined) params.append('min_cpa', filters.min_cpa.toString());
    if (filters.max_cpa !== undefined) params.append('max_cpa', filters.max_cpa.toString());
    if (filters.sort) params.append('sort', filters.sort);
    if (pagination.page) params.append('page', pagination.page.toString());
    if (pagination.page_size) params.append('per_page', pagination.page_size.toString());
    
    const response = await apiClient.get(`/creative-library?${params}`);
    return {
      creatives: response.data.creatives || [],
      total: response.data.pagination?.total || 0
    };
  },

  // Legacy getCreatives method for backwards compatibility
  async getCreatives(filters: CreativeFilters = {}): Promise<CreativeLibraryResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.platforms?.length) params.append('platforms', filters.platforms.join(','));
    if (filters.creative_types?.length) params.append('creative_types', filters.creative_types.join(','));
    if (filters.performance_tiers?.length) params.append('performance_tiers', filters.performance_tiers.join(','));
    if (filters.statuses?.length) params.append('statuses', filters.statuses.join(','));
    if (filters.tags?.length) params.append('tags', filters.tags.join(','));
    if (filters.min_roas) params.append('min_roas', filters.min_roas.toString());
    if (filters.max_cpa) params.append('max_cpa', filters.max_cpa.toString());
    if (filters.sort) params.append('sort', filters.sort);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    
    const response = await apiClient.get(`/creative-library?${params}`);
    return response.data;
  },

  // Get single creative with full details
  async getCreative(id: string): Promise<Creative & { usage_history: CreativeUsageHistory[] }> {
    const response = await apiClient.get(`/creative-library/${id}`);
    return response.data;
  },

  // Add tags to creative
  async addTags(creativeId: string, tags: string[]): Promise<void> {
    await apiClient.post(`/creative-library/${creativeId}/tags`, { tags });
  },

  // Remove tag from creative
  async removeTag(creativeId: string, tagName: string): Promise<void> {
    await apiClient.delete(`/creative-library/${creativeId}/tags/${tagName}`);
  },

  // Update creative notes
  async updateNotes(creativeId: string, notes: string): Promise<void> {
    await apiClient.patch(`/creative-library/${creativeId}`, { notes });
  },

  // Archive creative
  async archiveCreative(creativeId: string): Promise<void> {
    await apiClient.post(`/creative-library/${creativeId}/archive`);
  },

  // Reuse creative in campaign
  async reuseInCampaign(creativeId: string, campaignId: string, budget?: number): Promise<{
    new_ad_id: string;
    campaign_id: string;
  }> {
    const response = await apiClient.post(`/creative-library/${creativeId}/reuse`, {
      campaign_id: campaignId,
      budget
    });
    return response.data;
  },

  // Bulk tag creatives
  async bulkTag(creativeIds: string[], tags: string[]): Promise<void> {
    await apiClient.post('/creative-library/bulk-action', {
      creative_ids: creativeIds,
      action: 'add_tags',
      parameters: { tags }
    });
  },

  // Bulk add tags (alias for bulkTag)
  async bulkAddTags(creativeIds: string[], tags: string[]): Promise<void> {
    return this.bulkTag(creativeIds, tags);
  },

  // Bulk archive creatives
  async bulkArchive(creativeIds: string[]): Promise<void> {
    await apiClient.post('/creative-library/bulk-action', {
      creative_ids: creativeIds,
      action: 'archive',
      parameters: {}
    });
  },

  // Bulk export creatives
  async bulkExport(creativeIds: string[]): Promise<Blob> {
    const response = await apiClient.post('/creative-library/bulk-action', {
      creative_ids: creativeIds,
      action: 'export',
      parameters: { format: 'csv' }
    }, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get all tags
  async getTags(): Promise<Tag[]> {
    const response = await apiClient.get('/creative-library/tags');
    return response.data;
  },

  // Download creative asset
  getDownloadUrl(assetUrl: string): string {
    return assetUrl;
  }
};
